import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as modalController from "modalController";
import readlangClient from "readlangClient";
import Button from "ui/Button";
import Dialog from "views/Dialog";

interface RequestData {
  query: string;
  context: string;
  language: string;
  explanationLanguage: string;
}

interface Options {
  word: string;
  explanation: string;
  requestData: RequestData;
  directionStyle: preact.JSX.CSSProperties;
  /** Called when the user accepts a regenerated explanation */
  onAccepted: (newExplanation: string) => void;
  /** Called when the user chooses to hide the explanation for this word + context */
  onDismissed: () => void;
}

interface State {
  stage: "form" | "regenerating" | "verdict";
  feedback: string;
  reportID?: string;
  regeneratedExplanation?: string;
  /** True once the user has rejected a regenerated explanation, enabling the dismiss option */
  rejectedRegeneration?: boolean;
  error?: boolean;
}

class ExplanationReportModal extends preact.Component<Options, State> {
  state: State = { stage: "form", feedback: "" };

  renderExplanation(text: string, extraClass?: string) {
    return (
      <p
        class={["modalExplanation", extraClass].filter(Boolean).join(" ")}
        style={this.props.directionStyle}
      >
        <i class="fa icon-lightbulb"></i> &nbsp;{" "}
        <span class="preserveLineBreaks">{text}</span>
      </p>
    );
  }

  render() {
    switch (this.state.stage) {
      case "form":
        return this.renderForm();
      case "regenerating":
        return this.renderRegenerating();
      case "verdict":
        return this.renderVerdict();
    }
  }

  renderForm() {
    const { feedback, rejectedRegeneration, error } = this.state;

    return (
      <Dialog
        class="explanationReportModal"
        headingIcon="flag"
        heading="Report bad explanation"
        error={
          error ? "Problem regenerating explanation. Please try again." : null
        }
        actions={
          <>
            <button
              class="prominent"
              disabled={!feedback.trim()}
              onClick={() => this.submitReport()}
            >
              Regenerate explanation
            </button>
            {rejectedRegeneration ? (
              <button onClick={() => this.dismiss()}>Hide explanation</button>
            ) : null}
            <Button onClick={() => modalController.cancel()}>Cancel</Button>
          </>
        }
      >
        <p class="modalExplanationLabel">Current explanation:</p>
        {this.renderExplanation(this.props.explanation)}
        <p class="modalExplanationLabel">What's wrong with this explanation?</p>
        <textarea
          autoFocus={!deviceDetect.isSmall()}
          value={feedback}
          placeholder="e.g. The translation is wrong, it should mean..."
          onInput={(event) =>
            this.setState({ feedback: event.currentTarget.value })
          }
        />
      </Dialog>
    );
  }

  renderRegenerating() {
    return (
      <Dialog
        class="explanationReportModal"
        headingIcon="flag"
        heading="Report bad explanation"
      >
        <p class="modalExplanationLabel">Current explanation:</p>
        {this.renderExplanation(this.props.explanation)}
        <p class="modalRegenerating">Regenerating explanation...</p>
      </Dialog>
    );
  }

  renderVerdict() {
    return (
      <Dialog
        class="explanationReportModal"
        headingIcon="flag"
        heading="Report bad explanation"
        actions={
          <>
            <button class="prominent" onClick={() => this.accept()}>
              Yes, use it
            </button>
            <Button onClick={() => this.rejectRegeneration()}>No</Button>
          </>
        }
      >
        <p class="modalExplanationLabel">Original explanation:</p>
        {this.renderExplanation(this.props.explanation, "reported")}
        <p class="modalExplanationLabel">New explanation:</p>
        {this.renderExplanation(
          this.state.regeneratedExplanation,
          "regenerated"
        )}
        <p class="modalVerdictPrompt">Is this explanation better?</p>
      </Dialog>
    );
  }

  async submitReport() {
    this.setState({ stage: "regenerating", error: false });

    try {
      const response = await readlangClient.request<{
        reportID: string;
        explanation: string;
      }>("POST", "/api/smartDictionary/report", {
        ...this.props.requestData,
        badExplanation: this.props.explanation,
        feedback: this.state.feedback,
      });

      this.setState({
        stage: "verdict",
        reportID: response.data.reportID,
        regeneratedExplanation: response.data.explanation,
      });
    } catch (error: any) {
      console.error("Error reporting explanation: ", error);
      // On a terminal failure readlangClient already notified — restore the
      // form without the "Problem regenerating" banner.
      this.setState({ stage: "form", error: !error.jqXHR?.alreadyNotified });
    }
  }

  resolve(resolution: "accepted" | "dismissed") {
    readlangClient
      .request(
        "POST",
        `/api/smartDictionary/report/${this.state.reportID}/resolve`,
        { resolution }
      )
      .catch((error) => {
        console.error(`Error marking report ${resolution}: `, error);
      });
  }

  accept() {
    this.resolve("accepted");
    this.props.onAccepted(this.state.regeneratedExplanation);
    modalController.cancel(null, true);
  }

  rejectRegeneration() {
    this.setState({ stage: "form", rejectedRegeneration: true });
  }

  dismiss() {
    this.resolve("dismissed");
    this.props.onDismissed();
    modalController.cancel(null, true);
  }
}

export const show = (options: Options) => {
  modalController.show(<ExplanationReportModal {...options} />, {
    clickToCancel: true,
    closeButton: true,
  });
};
